import React, { useEffect, useState } from 'react';
import {
  Box,
  Dialog,
  Tabs,
  Tab,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from '@mui/material';
import { useAppBar } from '../../providers/AppBarProvider';
import { fetchPet, handleError, formatCurrency } from '../../utils/lib';
import { useLocation, useNavigate } from 'react-router-dom';
import { useConfig } from '../../providers/ConfigProvider';
import ViewPetInfo from './ViewPetInfo';
import ViewPetClinic from './ViewPetClinic';
import axios from 'axios';
import MedicalServicesOutlinedIcon from '@mui/icons-material/MedicalServicesOutlined';
import MedicalInformationOutlinedIcon from '@mui/icons-material/MedicalInformationOutlined';
import VaccinesIcon from '@mui/icons-material/Vaccines';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import MedicationLiquidIcon from '@mui/icons-material/MedicationLiquid';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import AddTaskIcon from '@mui/icons-material/AddTask';
import BottomSheet from '../../layouts/BottomSheet';
import PetsIcon from '@mui/icons-material/Pets';
import useNavigationHelperStore from '../../store/navigationHelperStore';
import ViewPetSupply from './ViewPetSupply';
import ViewPage from './ViewPage';
import { useSnackbar } from "../../providers/SnackBarProvider";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import useSellStore from '../../store/sellStore';
import useSupplyStore from '../../store/supplyStore';
import useAgendaStore from '../../store/agendaStore';
import useAgendaActions from '../../hooks/useAgendaActions';
import usePaymentDialog from '../../hooks/usePaymentDialog';
import usePetStore from '../../store/petStore';
import { useTranslation } from 'react-i18next';

function ViewPet() {
  const location = useLocation();
  const navigate = useNavigate();
  const { setNavigation } = useNavigationHelperStore();
  const snackbar = useSnackbar();
  const [id, setId] = useState(location.state.id);
  const [pet, setPet] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPdfLoading, setIsPdfLoading] = useState(false);
  const {updateTitle} = useAppBar();
  const [activeTab, setActiveTab] = useState(location.state?.activeTab ?? 0);
  const {config, isLoadingConfig} = useConfig();
  const { resetSell } = useSellStore();
  const { clearSupplies } = useSupplyStore();
  const { resetAgenda } = useAgendaStore();
  const { resetPet } = usePetStore();
  const { t } = useTranslation();

  // Usar el hook de acciones de agenda
  const {
    handleReschedule,
    handleComplete,
    AgendaDialogs
  } = useAgendaActions(
    // Callback onSuccess para recargar los datos del pet
    async () => {
      const updatedPet = await fetchPet(id, false);
      setPet(updatedPet);
    },
    snackbar
  );

  // Usar el hook de diálogo de pagos
  const {
    handleAddPayment,
    PaymentDialog
  } = usePaymentDialog(
    // Callback onSuccess para recargar los datos del pet
    async () => {
      const updatedPet = await fetchPet(id, false);
      setPet(updatedPet);
    },
    snackbar
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        const pet = await fetchPet(id, location.state?.updateLastView || false);
        setPet(pet);
      } catch (error) {
        handleError(error, snackbar);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  //carga el title y el avatarHandler
  useEffect(() => {
    if (pet) {
      const displayName =
        pet.death === 1 ? `${pet.name} | ${t('edit.decease')}` : pet.name;
      updateTitle(pet.thumb_url || '', displayName, pet.owners_es, false, PetsIcon, handleAvatarClick);
    }
  }, [pet, t]);

  const [deleteDialog, setDeleteDialog] = useState({
    open: false
  });

  const handleAvatarClick = () => {
    //console.log('Avatar clicked! Pet data:', pet);
    if (pet?.photo_url) {
      window.open(pet.photo_url, '_blank');
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleClinic = () => {
    setNavigation('/main/view-pet', { state: { id: pet.id, updateLastView: false, activeTab } });
    navigate('/main/edit-clinic', { state: { pet: pet } });
  };

  const handleExtendedClinic = () => {
    setNavigation('/main/view-pet', { state: { id: pet.id, updateLastView: false, activeTab } });
    navigate('/main/edit-clinic2', { state: { pet: pet } });
  };

  const handleClinicPdf = async () => {
    setIsPdfLoading(true);
    try {
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}pets_clinic?id_pet=${pet.id}&pdf`,
        { withCredentials: true }
      );
      
      const base64Data = response.data;
      const binaryString = atob(base64Data);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: 'application/pdf' });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = t('view.clinicalHistoryPdfFilename', { name: pet.name });
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      handleError(error, snackbar);
    } finally {
      setIsPdfLoading(false);
    }
  };

  const handleSupply = () => {
    clearSupplies();
    setNavigation('/main/view-pet', { state: { id: pet.id, updateLastView: false, activeTab } });
    navigate('/main/edit-supply', { state: { pet: pet } });
  };

  const handleStudy = () => {
    setNavigation('/main/view-pet', { state: { id: pet.id, updateLastView: false, activeTab } });
    navigate('/main/edit-study', { state: { pet: pet } });
  };

  const handleRecipe = () => {
    setNavigation('/main/view-pet', { state: { id: pet.id, updateLastView: false, activeTab } });
    navigate('/main/edit-recipe', { state: { pet: pet } });
  };

  const handleSale = () => {
    resetSell();
    setNavigation('/main/view-pet', { state: { id: pet.id, updateLastView: false, activeTab } });
    navigate('/main/edit-sell', { state: { pet: pet } });
  };

  const handleWaitingRoom = () => {
    setNavigation('/main/view-pet', { state: { id: pet.id, updateLastView: false, activeTab } });
    navigate('/main/edit-waiting-room', { state: { pet: pet } });
  };

  const handleAgenda = () => {
    resetAgenda();
    setNavigation('/main/view-pet', { state: { id: pet.id, updateLastView: false, activeTab } });
    navigate('/main/edit-agenda', { state: { pet: pet } });
  };

  const handleEdit = () => {
    resetPet();
    navigate('/main/edit-pet', { state: { updatePetFromLocation: pet } });
  };

  const handleDeleteOpen = () => {
    setDeleteDialog({
      open: true
    });
  };

  const handleDeleteClose = () => {
    setDeleteDialog({
      open: false
    });
  };

  const handleDeleteConfirm = async () => {
    try {
      await axios.delete(
        `${import.meta.env.VITE_API_URL}pets?id=${pet.id}`,
        { withCredentials: true }
      );
      
      handleDeleteClose();
      navigate(-2);
    } catch (error) {
      handleError(error, snackbar);
    }
  };

  const handleViewDebtDetail = (owner) => {
    navigate('/main/view-owner-debts', { 
      state: { 
        id: owner.id,
        name: owner.name,
        thumb_url: owner.thumb_url
      } 
    });
  };

  if (isLoading || isLoadingConfig || !pet) {
    return <ViewPage isLoading={true} />;
  }

  return (
    <ViewPage
      isLoading={isLoading || isLoadingConfig || isPdfLoading}
    >
      <Box sx={{ width: '100%', m: 0, p: 0 }}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange}
          variant="fullWidth"
          textColor="inherit"
          indicatorColor="primary"
          sx={{ minHeight: '48px' }}
        >
          <Tab label={config.vet.pet_naming_es} sx={{ minHeight: '48px' }} />
          <Tab label={t('view.clinic')} sx={{ minHeight: '48px' }} />
          <Tab label={t('view.supply')} sx={{ minHeight: '48px' }} />
        </Tabs>

        {activeTab === 0 && (
          <Box sx={{ px: 1 }}>
            <ViewPetInfo
              pet={pet}
              onPostpone={handleReschedule}
              onComplete={handleComplete}
              onViewDebtDetail={handleViewDebtDetail}
              onAddPayment={handleAddPayment}
            />
          </Box>
        )}
        {activeTab === 1 && (
          <Box sx={{ px: 1 }}>
            <ViewPetClinic
              pet={pet}
            />
          </Box>
        )}
        {activeTab === 2 && (
          <Box sx={{ px: 1 }}>
            <ViewPetSupply
              pet={pet}
            />
          </Box>
        )}
      </Box>

      <AgendaDialogs />

      <Dialog
        open={deleteDialog.open}
        onClose={handleDeleteClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('view.confirmDeletePetTitle', { name: pet?.name })}</DialogTitle>
        <DialogContent>
          {t('view.confirmDeletePetMessage', { name: pet?.name })}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteClose}>{t('common.cancel')}</Button>
          <Button onClick={handleDeleteConfirm} variant="contained" color="error">
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {PaymentDialog}

      <BottomSheet 
        zIndex={1001}
        buttonGroups={[
          {
            label: t('common.actions'),
            buttons: [
              ...(config.permissions.actions_permissions.pets_clinic.create ? [{
                label: t('view.petActionClinic'),
                action: handleClinic,
                icon: <MedicalServicesOutlinedIcon />
              }] : []),
              ...(config.permissions.actions_permissions.pets_clinic.create ? [{
                label: t('view.petActionExtendedClinic'),
                action: handleExtendedClinic,
                icon: <MedicalInformationOutlinedIcon />
              }] : []),
              ...(config.permissions.actions_permissions.pets_clinic.create ? [{
                label: t('view.petActionSupply'),
                action: handleSupply,
                icon: <VaccinesIcon />
              }] : []),
              ...(config.permissions.actions_permissions.pets_clinic.create ? [{
                label: t('view.petActionStudy'),
                action: handleStudy,
                icon: <ScienceOutlinedIcon />
              }] : []),
              ...(config.permissions.actions_permissions.pets_clinic.create ? [{
                label: t('view.petActionRecipe'),
                action: handleRecipe,
                icon: <MedicationLiquidIcon />
              }] : []),
              ...(config.permissions.actions_permissions.sells.create ? [{
                label: t('main.sell'),
                action: handleSale,
                icon: <PointOfSaleIcon />
              }] : []),
              ...(config.permissions.actions_permissions.agenda.create ? [{
                label: t('main.agenda'),
                action: handleAgenda,
                icon: <AddTaskIcon />
              }] : []),
              ...(config.permissions.actions_permissions.pets_clinic.create ? [{
                label: t('view.petActionClinicPdf'),
                action: handleClinicPdf,
                icon: <PictureAsPdfIcon />
              }] : []),
            ].filter(Boolean)
          },
          {
            label: config.vet.pet_naming_es,
            buttons: [
              ...(config.permissions.actions_permissions.pets.update ? [{
                label: t('view.vetActionEdit'),
                action: handleEdit,
                icon: <EditIcon />
              }] : []),
              ...(config.permissions.actions_permissions.pets.delete ? [{
                label: t('common.delete'),
                action: handleDeleteOpen,
                icon: <DeleteIcon />
              }] : []),
            ].filter(Boolean)
          },
        ]}
      />

    </ViewPage>
  );
}

export default ViewPet;

